<template>
  <div class="project-list">
    <div
      class="project-container"
      :class="{ 'has-session-bar': isShowSessionSwitcher }"
    >
      <v-expansion-panels v-model="tab" accordion multiple>
        <v-expansion-panel
          v-for="(item, i) in items"
          :key="`company_${i}`"
          class="!tw-py-0"
          :disabled="!companiesItemsByTab(i).length"
          :class="{ 'tw-opacity-50': !companiesItemsByTab(i).length }"
          @click="onChangeTab(i)"
        >
          <v-expansion-panel-header
            class="!tw-flex !tw-flex-row-reverse !tw-justify-between hover:tw-bg-bg-2 !tw-pl-2 !tw-rounded-lg"
          >
            <div class="tw-flex tw-gap-2 tw-items-center">
              <div><img :src="item.icon" width="20" /></div>
              <div class="tw-flex tw-items-center tw-gap-2">
                <Typography
                  :variant="tab.includes(i) ? 'subtitle-2' : 'body-2'"
                >
                  {{ item.title }} ({{
                    getProjectCount(companiesItemsByTab(i)).length
                  }})
                </Typography>
              </div>
            </div>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <ProjectGroup
              :projectId="projectId"
              :companiesItems="companiesItemsByTab(i)"
              :isActive="activeIndex === i"
              @openProject="openProject"
              @getTop="$emit('getTop')"
              @isShowActions="$emit('isShowActions', $event)"
              @triggerProjectId="$emit('triggerProjectId', $event)"
            />
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
    </div>
  </div>
</template>
<script>
import ProjectGroup from '@/components/widgets/ProjectGroup';
import UserVerified from '@/assets/icons/user-check-01.svg';
import SharedIcon from '@/assets/icons/share-06.svg';
import PausedIcon from '@/assets/icons/pause-circle.svg';
import ArchivedIcon from '@/assets/icons/archive.svg';
import Typography from '@/components/v3/base/Typography';

export default {
  name: 'ProjectList',
  components: {
    ProjectGroup,
    Typography,
  },

  props: {
    projectId: String,
    isShowSessionSwitcher: Boolean,
  },

  data() {
    const items = [
      {
        title: 'Active',
        icon: UserVerified,
      },
      {
        title: 'Processing',
        icon: SharedIcon,
      },
      {
        title: 'Paused',
        icon: PausedIcon,
      },
      {
        title: 'Archived',
        icon: ArchivedIcon,
      },
    ];
    if (this.$store.getters['user/getIsUserV2']) items.push('Draft');
    return {
      tab: [0],
      items,
      activeIndex: 0,
      processingStatuses: ['4', '5', '6'],
    };
  },

  computed: {
    projects() {
      return this.$store.getters['projects/listProjects'] || {};
    },

    activeProjects() {
      return this.projects.filter((proj) => {
        const activeStatuses = ['1'];
        return this.isV2
          ? activeStatuses.includes(proj.status)
          : !proj.is_processing && !proj.outreach_pause && !proj.is_archived;
      });
    },

    processingProject() {
      return this.projects.filter(
        (proj) =>
          (proj.is_processing && !proj.is_archived) ||
          this.processingStatuses.includes(proj.status)
      );
    },

    projectsPaused() {
      return this.projects.filter(
        (proj) =>
          (proj.outreach_pause && !proj.is_archived && !proj.is_processing) ||
          proj.status === '2'
      );
    },

    projectArchived() {
      return this.projects.filter(
        (proj) => proj.is_archived || proj.status === '7'
      );
    },

    projectsDraft() {
      return this.projects.filter((proj) => proj.status === '3');
    },

    currentProject() {
      return this.projects.filter(
        (item) => item.project_id === this.projectId
      )[0];
    },
  },

  watch: {
    projects(v, oldv) {
      const diff = Object.keys(v).reduce((dif, key) => {
        if (oldv[key] === v[key]) return dif;
        return {
          ...dif,
          [key]: v[key],
        };
      }, {});
      if (Object.keys(diff).length === 0) return;
      this.checkProjectId();
      this.getTab();
    },

    projectId(v, oldv) {
      this.checkProjectId();
      this.getTab();
    },
  },

  mounted() {
    this.checkProjectId();
    this.getTab();
  },

  methods: {
    onChangeTab(v) {
      this.activeIndex = v;
    },

    openProject(id) {
      this.$emit('open', id);
    },

    getSelectedTab() {
      if (
        this.currentProject &&
        ((this.currentProject.is_processing &&
          !this.currentProject.is_archived) ||
          this.processingStatuses.includes(this.currentProject.status))
      ) {
        return [1];
      }
      if (
        this.currentProject &&
        ((this.currentProject.outreach_pause &&
          !this.currentProject.is_archived) ||
          this.currentProject.status === '2')
      ) {
        return [2];
      }
      if (
        this.currentProject &&
        (this.currentProject.is_archived || this.currentProject.status === '7')
      ) {
        return [3];
      }
      return [0];
    },

    checkProjectId() {
      if (this.projectId) {
        this.openProject(this.projectId);
        return;
      }
      if (this.activeProjects && this.activeProjects.length) {
        this.openProject(this.activeProjects[0].project_id);
        return;
      }
      if (this.processingProject && this.processingProject.length) {
        this.openProject(this.processingProject[0].project_id);
        return;
      }
      if (this.projectsPaused && this.projectsPaused.length) {
        this.openProject(this.projectsPaused[0].project_id);
        return;
      }
      if (this.projectArchived && this.projectArchived.length) {
        this.openProject(this.projectArchived[0].project_id);
      }
    },

    companiesItemsByTab(tab) {
      let arr = this.activeProjects;
      switch (tab) {
        case 0:
          arr = this.activeProjects;
          break;

        case 1:
          arr = this.processingProject;
          break;

        case 2:
          arr = this.projectsPaused;
          break;

        case 3:
          arr = this.projectArchived;
          break;

        case 4:
          arr = this.projectsDraft;
          break;

        default:
          break;
      }
      arr = arr.sort((a, b) => {
        const c = a.hiring_company.toUpperCase().trim();
        const d = b.hiring_company.toUpperCase().trim();
        if (c < d) {
          return -1;
        }
        if (c > d) {
          return 1;
        }
        return 0;
      });
      const groups = [];
      let ind = 0;
      arr.forEach((item) => {
        if (
          groups.length &&
          !(
            groups[ind] &&
            groups[ind].projects &&
            groups[ind].projects.filter(
              (e) =>
                e.hiring_company.toUpperCase().trim() ===
                item.hiring_company.toUpperCase().trim()
            ).length
          )
        ) {
          ind += 1;
        }
        if (!groups[ind]) {
          groups[ind] = {};
          groups[ind].projects = [];
          groups[ind].hiring_company = item.hiring_company;
          groups[ind].model = false;
        }
        groups[ind].projects.push(item);
      });
      return groups;
    },

    getProjectCount(val) {
      const arr = [];
      val.forEach((item) => {
        item.projects.forEach((project) => {
          arr.push(project);
        });
      });
      return arr;
    },

    getTab() {
      if (this.tab.length > 1) {
        const tabs = this.getSelectedTab();
        tabs.forEach((item) => {
          if (!this.tab.includes(item)) {
            this.tab.push(item);
          }
        });
      } else {
        this.tab = this.getSelectedTab();
      }
    },
  },
};
</script>
<style lang="scss" scoped>
@import './styles';
.theme--light.v-expansion-panels .v-expansion-panel:not(:first-child):after {
  display: none;
}
::v-deep {
  .v-expansion-panel-header__icon {
    position: absolute;
    right: 0px;
  }
  .v-list-item {
    min-height: 38px !important;
  }
  .v-list-item__icon.icon1 {
    margin-top: 5px !important;
  }
  .action-item {
    top: 8px !important;
  }
  .v-expansion-panel-header__icon .v-icon.v-icon {
    font-size: 20px !important;
  }
}
</style>
